import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Link2, Search, ChevronDown, ChevronUp, Loader2, ExternalLink } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { extractError } from '../../lib/types.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ShortLink {
  urlCode: string;
  originalUrl: string;
  shortUrl: string;
}

interface ShortLinksResponse {
  items: ShortLink[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (shortUrl: string) => void;
}

// ─── ShortlinkPickerModal ─────────────────────────────────────────────────────

export function ShortlinkPickerModal({ isOpen, onClose, onSelect }: Props) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createUrl, setCreateUrl] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  // ── Query ──────────────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useQuery<ShortLinksResponse>({
    queryKey: ['shortlinks-picker'],
    queryFn: () =>
      api.get('/api/v1/shortlinks?page=1&pageSize=200').then((r) => r.data),
    enabled: isOpen,
    staleTime: 15_000,
  });

  const items = data?.items ?? [];

  // ── Client-side filter ─────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter(
        (link) =>
          link.originalUrl.toLowerCase().includes(q) ||
          link.urlCode.toLowerCase().includes(q),
      )
    : items;

  // ── Create mutation ────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (payload: { originalUrl: string; urlCode?: string }) =>
      api.post('/api/v1/shortlinks', payload).then((r) => r.data),
    onSuccess: (created: ShortLink) => {
      qc.invalidateQueries({ queryKey: ['shortlinks'] });
      qc.invalidateQueries({ queryKey: ['shortlinks-picker'] });
      onSelect(created.shortUrl);
      handleClose();
    },
    onError: (err) => setCreateError(extractError(err)),
  });

  function handleSelect(link: ShortLink) {
    onSelect(link.shortUrl);
    onClose();
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!createUrl.trim()) return;
    setCreateError(null);
    const payload: { originalUrl: string; urlCode?: string } = {
      originalUrl: createUrl.trim(),
    };
    if (createSlug.trim()) payload.urlCode = createSlug.trim();
    createMutation.mutate(payload);
  }

  function handleClose() {
    setSearch('');
    setCreateOpen(false);
    setCreateUrl('');
    setCreateSlug('');
    setCreateError(null);
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative z-10 bg-white rounded-2xl shadow-xl border border-zinc-200 w-full max-w-lg flex flex-col overflow-hidden max-h-[85vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-zinc-100 rounded-lg">
              <Link2 size={15} className="text-zinc-600" />
            </div>
            <h2 className="text-sm font-semibold text-zinc-900">Inserisci shortlink</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
            aria-label="Chiudi"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-5 py-3 border-b border-zinc-100 flex-shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per URL o codice…"
              className="w-full h-9 pl-9 pr-3 border border-zinc-200 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              autoFocus
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-zinc-400 gap-2">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-sm">Caricamento…</span>
            </div>
          )}

          {isError && (
            <div className="text-center py-12 text-sm text-red-500 px-5">
              Errore nel caricamento degli shortlink.
            </div>
          )}

          {!isLoading && !isError && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-400 gap-2">
              <Link2 size={32} className="opacity-30" />
              <p className="text-sm">
                {q ? 'Nessun risultato per questa ricerca.' : 'Nessun shortlink disponibile.'}
              </p>
            </div>
          )}

          {!isLoading && !isError && filtered.length > 0 && (
            <ul className="divide-y divide-zinc-100">
              {filtered.map((link) => (
                <li key={link.urlCode}>
                  <button
                    onClick={() => handleSelect(link)}
                    className="w-full px-5 py-3 text-left hover:bg-zinc-50 transition-colors group"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-mono text-blue-600 truncate group-hover:text-blue-700">
                          {link.shortUrl}
                        </p>
                        <p className="text-xs text-zinc-400 truncate mt-0.5 flex items-center gap-1">
                          <ExternalLink size={10} className="flex-shrink-0" />
                          {link.originalUrl}
                        </p>
                      </div>
                      <span className="flex-shrink-0 text-[11px] font-mono text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded">
                        {link.urlCode}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Create section */}
        <div className="flex-shrink-0 border-t border-zinc-100">
          <button
            onClick={() => setCreateOpen((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
          >
            <span>Crea nuovo</span>
            {createOpen ? <ChevronUp size={15} className="text-zinc-400" /> : <ChevronDown size={15} className="text-zinc-400" />}
          </button>

          {createOpen && (
            <form onSubmit={handleCreate} className="px-5 pb-5 space-y-3">
              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1">
                  URL destinazione <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={createUrl}
                  onChange={(e) => setCreateUrl(e.target.value)}
                  placeholder="https://esempio.com/pagina"
                  required
                  className="w-full h-9 px-3 border border-zinc-200 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1">
                  Slug custom{' '}
                  <span className="text-zinc-400 font-normal">(opzionale)</span>
                </label>
                <input
                  type="text"
                  value={createSlug}
                  onChange={(e) => setCreateSlug(e.target.value)}
                  placeholder="es. promo-estate"
                  className="w-full h-9 px-3 border border-zinc-200 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
              </div>

              {createError && (
                <p className="text-xs text-red-500">{createError}</p>
              )}

              <button
                type="submit"
                disabled={createMutation.isPending || !createUrl.trim()}
                className="flex items-center gap-2 px-4 py-1.5 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createMutation.isPending && <Loader2 size={13} className="animate-spin" />}
                Crea e inserisci
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
